// Server-side analytics tracking service
// This complements GA4 by tracking visits server-side (bypasses cookie blockers)

class ServerAnalyticsService {
  private static instance: ServerAnalyticsService;
  private trackingEnabled = true;
  private lastTrackedPath: string | null = null;
  private trackingCooldown = 5000; // Don't track same path more than once per 5 seconds

  private constructor() {}

  static getInstance(): ServerAnalyticsService {
    if (!ServerAnalyticsService.instance) {
      ServerAnalyticsService.instance = new ServerAnalyticsService();
    }
    return ServerAnalyticsService.instance;
  }

  // Get the base URL and API path for API calls
  private getApiPath(functionName: string): string {
    // Always use direct function path - more reliable than redirects
    // The redirect rule in netlify.toml may not work in all cases
    return `/.netlify/functions/${functionName}`;
  }

  // Track a page view
  async trackPageView(path: string = window.location.pathname): Promise<void> {
    if (!this.trackingEnabled) return;
    
    // Prevent duplicate tracking
    if (this.lastTrackedPath === path) {
      return;
    }
    this.lastTrackedPath = path;

    try {
      const apiPath = this.getApiPath('track-visit');
      const url = `${apiPath}?path=${encodeURIComponent(path)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.warn('Failed to track visit:', response.status);
      }
    } catch (error) {
      // Silently fail - don't break the app if analytics fails
      console.debug('Analytics tracking error:', error);
    }
  }

  // Track a custom event (optional, for future use)
  async trackEvent(eventName: string, data?: Record<string, any>): Promise<void> {
    if (!this.trackingEnabled) return;

    try {
      const apiPath = this.getApiPath('track-visit');
      const url = apiPath;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          path: window.location.pathname,
          event: eventName,
          ...data
        })
      });

      if (!response.ok) {
        console.warn('Failed to track event:', response.status);
      }
    } catch (error) {
      console.debug('Analytics tracking error:', error);
    }
  }

  // Get analytics data (for admin page)
  async getAnalytics(days: number | 'today' | 'yesterday' = 30, authToken: string): Promise<any> {
    try {
      // Get the correct API path based on environment
      const apiPath = this.getApiPath('get-analytics');
      // Add cache-busting parameter to force fresh request
      const timestamp = Date.now();
      const daysParam = typeof days === 'string' ? days : days.toString();
      const url = `${apiPath}?days=${daysParam}&_t=${timestamp}`;
      
      console.log('Fetching analytics from:', url);
      console.log('API Path:', apiPath);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('Request timeout after 30 seconds');
        controller.abort();
      }, 30000); // 30 second timeout
      
      const startTime = Date.now();
      console.log('Starting fetch request...');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        cache: 'no-store', // Prevent browser caching
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      console.log(`Fetch completed in ${duration}ms, status:`, response.status);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized - check your analytics token');
        }
        
        // Try to get error message from response
        let errorMessage = `Failed to fetch analytics: ${response.status}`;
        let errorData: string | null = null;
        try {
          errorData = await response.text();
          // Check if it's HTML (error page)
          if (errorData.trim().startsWith('<!DOCTYPE') || errorData.trim().startsWith('<html')) {
            errorMessage = 'Function not available. Make sure you\'re running `netlify dev` (not `npm start`)';
          } else {
            const parsed = JSON.parse(errorData);
            errorMessage = parsed.message || parsed.error || errorMessage;
            // Include details if available in dev mode
            if (parsed.details) {
              errorMessage += ` (${parsed.details})`;
            }
          }
        } catch (parseError) {
          // If we can't parse, use the raw text (truncated)
          const errorText = errorData?.substring(0, 200) || 'Unknown error';
          errorMessage = `Server error: ${errorText}`;
        }
        
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);
      
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received:', text.substring(0, 500));
        throw new Error(`Expected JSON but got ${contentType}. Response: ${text.substring(0, 200)}`);
      }

      const jsonData = await response.json();
      console.log('Successfully parsed JSON response:', jsonData);
      return jsonData;
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      
      // Handle timeout
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. The function may be taking too long or there may be a connection issue.');
      }
      
      throw error;
    }
  }
}

export const serverAnalytics = ServerAnalyticsService.getInstance();

